import type { AddressDTO } from './Address';

export const ShippingStatus = {
  created: 'CREADO',
  registered: 'REGISTRADO',
  for_pickup: 'POR_RECOGER',
  pickup_authorized: 'AUTORIZADO',
  picked: 'RECOGIDO',
  confirmed: 'CONFIRMADO',
  processing: 'PROCESANDO',
  ready: 'READY',
  shipped: 'DESPACHADO',
  paying: 'PAGADO_COMPRADOR',
  paid: 'PAGADO_ADMIN',
  paid_order: 'PAGADO_ORDEN',
  closed: 'CERRADO',
  blocked: 'BLOQUEADO',
} as const;

export type ShippingStatusType = typeof ShippingStatus[keyof typeof ShippingStatus];

// Document types for pickup confirmation
export const DocumentType = {
  dni: 'DNI',
  ce: 'CE',
} as const;

export type DocumentTypeValue = (typeof DocumentType)[keyof typeof DocumentType];

// Valorem range info (del backend: envío REGISTRADO y, en la configuración del envío, cada acuerdo)
export interface ValoremRange {
  enabled: boolean; // false si total_deals < 100
  min: number; // 100 si el total ≤ S/ 500; si no, 50 % del total (tope S/ 10,000)
  max: number; // floor del total, tope S/ 10,000
  deals_total: number; // Suma de todos los deals del envío
  insurance_rate: number; // Tasa del seguro: 0.006 (0.6%) o 0.02 (2%)
  insurance_igv: number; // IGV: 0.18 (18%)
  required?: boolean; // true si el total supera S/ 1,000: asegurar es obligatorio (sin la clave = no obligatorio)
}

// Unified payload for shipping confirmation
export interface ConfirmShippingPayload {
  shipping_id: number;
  // Modalidad Envío a dirección
  delivery_address_id?: number;
  valorem?: number;
  // Documento de quien recibe: solo si la dirección aún no lo tiene
  document_type?: DocumentTypeValue;
  document_number?: string;
  // Modalidad Recojo en persona
  is_pickup?: boolean;
  pickup_full_name?: string;
  pickup_document_type?: DocumentTypeValue;
  pickup_document_number?: string;
}

// Pickup office info (where to pick up the shipping)
export interface PickupOffice {
  name: string; // e.g., "Mailboxes ETC"
  address: string; // e.g., "Av. Javier Prado Este 123, San Isidro"
}

interface ShippingDTO {
  pickup_office: PickupOffice | null;
  delivery_address: any;
  dir_id: number | null;
  current_status: ShippingStatusType;
  carrier_tracking_code: string | null;
  fecblq: string | null;
  feccre: string;
  fecpagadm: string | null;
  paying_at: string | null;
  paid_at?: string | null;
  for_pickup_at?: string | null;
  id: number;
  idu: number;
  imp: number | null;
  amount?: number | null;
  is_affected: boolean | null;
  is_closed_debited: boolean | null;
  registered_at?: string | null;
  picked_at?: string | null;
  picked_by_name?: string | null;
  picked_by_doc_type?: DocumentTypeValue | null;
  picked_by_doc_number?: string | null;
  confirmed_at?: string | null;
  processing_at?: string | null;
  ready_at?: string | null;
  shipped_at?: string | null;
  closed_at?: string | null;
  currency: 'SOLES' | 'DOLARES';
  description: string;
  shipping_details: any[];
  tca: string;
  total: number | null;
  carrier_cost: number | null;
  carrier_id: number;
  created_at?: string | null;
  updated_at?: string | null;
  deleted_at?: string | null;
  // Valor asegurado elegido al confirmar (null = sin seguro). Llega como string ("516.00", DECIMAL sin cast)
  valorem?: number | string | null;
  // Valorem range - only present when current_status === 'REGISTRADO'
  valorem_range?: ValoremRange;
  // Configuración del envío elegida por el comprador, para precargar Confirmar Envío - only REGISTRADO
  preconfirm_hint?: PreconfirmHint;
}

export default ShippingDTO;

/*
 * Configuración del envío nacional (SPEC_SHIPPING_PRECONFIRM §3.4–§3.9). En la UI es "Configurar envío";
 * en el código y la API se sigue llamando `preconfirm`.
 */

// Configuración de los acuerdos del envío, para precargar el drawer de Confirmar Envío
export interface PreconfirmHint {
  delivery_address_id: number | null; // dirección que comparten los acuerdos configurados, si sigue vigente
  valorem: number | null; // suma de los seguros elegidos, acotada al rango del envío; null = sin seguro
}

// Envío más reciente que contiene al acuerdo, en cualquier estado (incluye CERRADO)
export interface PreconfirmShipping {
  id: number;
  current_status: ShippingStatusType;
  district?: string | null; // distrito de la dirección confirmada del envío
  is_pickup?: boolean; // recojo en persona
  is_shipped?: boolean; // ya salió, aunque siga en PAGADO_* (se puede pagar después del despacho)
}

// Objeto `preconfirm` del acuerdo (solo con envío nacional). Los campos opcionales pueden faltar con un
// backend anterior: se comparan por truthiness
export interface DealPreconfirm {
  is_eligible: boolean; // se puede configurar: en tránsito, sin envío abierto o con su envío en CREADO
  address_id: number | null; // dirección configurada (columna cruda: puede ser historial)
  district?: string | null; // distrito de address_id, solo si la dirección sigue vigente
  valorem: number | null; // seguro elegido; 0 = sin seguro
  preconfirmed_at: string | null;
  valorem_range: ValoremRange; // sobre el total del acuerdo
  shipping: PreconfirmShipping | null;
}

// Acuerdo del resumen (GET /user/shipping/preconfirm)
export interface PreconfirmDeal {
  ida: number;
  status_label: string;
  total_soles: number;
  preconfirmed_valorem: number | null;
  preconfirmed_at: string | null;
  valorem_range: ValoremRange;
  order: {
    tit: string | null;
    img: string | null;
    extra_image_url: string | null;
    img_path: string | null;
  };
  traveler: { mask_fullname: string | null };
}

// Acuerdos configurados a una misma dirección. La UI no los usa: el backend los conserva por si vuelven (§3.6)
export interface PreconfirmGroup {
  address: AddressDTO;
  deals: PreconfirmDeal[];
  deals_total: number;
  valorem_total: number; // suma sin tope
  insurance_rate: number;
  insurance_igv: number;
  estimated_cost: number; // ya con el tope
}

// Tarifas del seguro; up_to es inclusivo
export interface InsuranceTiers {
  min_enabled: number;
  cap: number;
  igv: number;
  rates: { up_to: number | null; rate: number }[];
}

export interface PreconfirmSummary {
  groups: PreconfirmGroup[];
  pending_deals: PreconfirmDeal[];
  tiers: InsuranceTiers;
  // Para el aviso de envío conjunto (D10)
  context?: { transit_deals: number; open_shipments: number };
}

export interface PreconfirmPayload {
  delivery_address_id: number;
  deals: { ida: number; valorem: number }[]; // valorem 0 = sin seguro
  // Documento de quien recibe: solo si la dirección aún no lo tiene
  document_type?: DocumentTypeValue;
  document_number?: string;
}

// Clic en el badge del acuerdo: abrir el drawer "Configurar envío" o el modal de Mis Envíos
export type ShippingConfigAction = 'configure' | 'manage' | null;

export interface ShippingConfigBadgeState {
  text: string;
  action: ShippingConfigAction;
  editIcon: boolean; // configurable y todavía sin dirección elegida
}

const NATIONAL_SHIPPING = 'Envío Nacional';

const badgeState = (
  text: string,
  action: ShippingConfigAction = null,
  editIcon = false,
): ShippingConfigBadgeState => ({ text, action, editIcon });

/**
 * Estado del badge "Envío Nacional" del acuerdo (§7.2), en orden de evaluación. Sin flag_envio no hay
 * badge: lo decide el llamador.
 */
export const shippingConfigBadge = (preconfirm?: DealPreconfirm | null): ShippingConfigBadgeState => {
  // 1. Sin datos de configuración (acuerdo no pagado, otras pestañas o backend viejo)
  if (!preconfirm) return badgeState(NATIONAL_SHIPPING);
  // 2 y 3. En tránsito: se configura desde el badge
  if (preconfirm.is_eligible) {
    return preconfirm.district
      ? badgeState(`Se enviará a ${preconfirm.district}`, 'configure')
      : badgeState(NATIONAL_SHIPPING, 'configure', true);
  }
  const shipping = preconfirm.shipping;
  // 8. Sin envío y ya no editable
  if (!shipping) return badgeState(NATIONAL_SHIPPING);
  const status = shipping.current_status;
  // 4 y 5. Recojo en persona
  if (shipping.is_pickup) {
    return status === ShippingStatus.picked || status === ShippingStatus.closed
      ? badgeState('Recogido')
      : badgeState('Recojo en oficina', 'manage');
  }
  const district = shipping.district || preconfirm.district;
  // 6. Ya despachado (is_shipped cubre los PAGADO_* que ya salieron)
  if (
    shipping.is_shipped ||
    status === ShippingStatus.shipped ||
    status === ShippingStatus.closed
  ) {
    return badgeState(district ? `Enviado a ${district}` : 'Enviado');
  }
  // 7. En la oficina de envíos: se gestiona en Mis Envíos
  return badgeState(district ? `Enviando a ${district}` : NATIONAL_SHIPPING, 'manage');
};
